import { useEffect, useState } from 'react';

import { api } from '../api/client';
import { ChartRenderer } from '../components/ChartRenderer';
import type { SavedChart, SavedDashboard } from '../types';

/**
 * What the platform embeds: `/d/<slug>?app_id=…` (or `app_ids=…`).
 *
 * The app filter arrives as URL params exactly as it did with Superset, so the
 * platform's existing app-picker plumbing keeps working untouched.
 */
interface Props {
  slug: string;
}

const filterParams = (): Record<string, string> => {
  const search = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};
  for (const key of ['app_id', 'app_ids', 'platform', 'category']) {
    const value = search.get(key);
    if (value) params[key] = value;
  }
  return params;
};

export const DashboardView = ({ slug }: Props) => {
  const [dashboard, setDashboard] = useState<SavedDashboard | null>(null);
  const [charts, setCharts] = useState<SavedChart[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.dashboard(slug), api.charts()])
      .then(([d, c]) => {
        setDashboard(d);
        setCharts(c);
      })
      .catch((err: Error) => setError(err.message));
  }, [slug]);

  if (error) {
    return (
      <div className="chart-error">
        <strong>Report unavailable</strong>
        <span>{error}</span>
      </div>
    );
  }

  if (!dashboard) return <div className="chart-loading">Loading report…</div>;

  const overrides = filterParams();
  const needsApp = dashboard.appFilter !== 'none';
  const hasApp = Boolean(overrides.app_id || overrides.app_ids);

  if (needsApp && !hasApp) {
    return <div className="chart-empty">Select {dashboard.appFilter === 'single' ? 'an app' : 'apps'} to view data.</div>;
  }

  return (
    <div className="dashboard-view">
      <div className="dashboard-grid">
        {dashboard.tiles.map((tile, i) => {
          const chart = charts.find((c) => c.id === tile.chartId);
          if (!chart) return null;
          return (
            <div
              className="dashboard-tile"
              key={`${tile.chartId}-${i}`}
              style={{ gridColumn: `span ${tile.width}`, minHeight: tile.height * 56 }}
            >
              <h4>{tile.title ?? chart.name}</h4>
              <ChartRenderer chart={chart} overrideParams={overrides} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Single-chart embed for widget tiles: `/w/<widgetId>`. */
export const WidgetView = ({ widgetId }: { widgetId: string }) => {
  const [chart, setChart] = useState<SavedChart | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.widgets(), api.charts()])
      .then(([widgets, charts]) => {
        const widget = widgets.find((w) => w.id === widgetId);
        if (!widget) throw new Error('Widget not found');
        const found = charts.find((c) => c.id === widget.chartId);
        if (!found) throw new Error('The widget points at a chart that no longer exists');
        setChart(found);
      })
      .catch((err: Error) => setError(err.message));
  }, [widgetId]);

  if (error) {
    return (
      <div className="chart-error">
        <strong>Widget unavailable</strong>
        <span>{error}</span>
      </div>
    );
  }

  if (!chart) return <div className="chart-loading">Loading…</div>;

  return (
    <div className="widget-view">
      <ChartRenderer chart={chart} overrideParams={filterParams()} />
    </div>
  );
};
